/**
 * กระดาษทดเขียนมือ (D7)
 *
 * ★★ สามอย่างนี้พิสูจน์แล้วบนเครื่องจริง (ตอบสนอง 7 ms เกณฑ์คือไม่เกิน 50)
 *     **ห้ามเปลี่ยนวิธีโดยไม่มีเหตุผลและไม่วัดใหม่**
 *
 *  1. **Pointer Events + ตรวจ `pointerType`** — เจอ `"pen"` เมื่อไหร่ ให้ปฏิเสธ
 *     `"touch"` ทันที นี่คือการกันฝ่ามือ ไม่ใช่ heuristic เรื่องขนาดสัมผัส
 *     ต้องมีสวิตช์ "วาดด้วยนิ้ว" เพราะไม่ใช่เด็กทุกคนมี Apple Pencil
 *
 *  2. **`getCoalescedEvents()` ตอน `pointermove`** — นี่คือสาเหตุที่เส้นคม
 *     iPad ส่ง pointer 240 Hz แต่ browser ยิง event ตาม frame (60 Hz)
 *     ถ้าไม่ดึงจุดที่ถูกยุบรวมออกมา จะเสียจุดไป 3 ใน 4 แล้วเส้นเป็นเหลี่ยม
 *
 *  3. **วาดเฉพาะเส้นใหม่ตอน move · redraw ทั้งหมดเฉพาะตอน undo / resize**
 *     ถ้าเปลี่ยนไป redraw ทุก frame จะหน่วงทันทีเมื่อเส้นเยอะ
 *     (ต้นทุนโตตามจำนวนจุดสะสม ไม่ใช่ตามจุดใหม่)
 *
 * ⚠️ `touch-action: none` อยู่บน **กระดาษทดกับแถบเครื่องมือของมันเท่านั้น**
 *   (`.padwrap` / `.sketch` ใน design.css) ถ้าไปใส่ที่ทั้งหน้า เด็กจะเลื่อนหน้าจอไม่ได้
 *
 * ★★ **การเมินไม่เท่ากับการกิน** (บั๊กจริงบน iPad 8 ก.ย. 2569 23:41)
 *   เดิมตอน "วาดด้วยนิ้ว: ปิด" โค้ด `return` ออกไปเฉย ๆ เมื่อ pointerType
 *   ไม่ใช่ pen — เมินเหตุการณ์ แต่ไม่ได้ `preventDefault()` เหตุการณ์จึงไหล
 *   ต่อไปให้ Safari จัดการเป็น "กดค้างเพื่อเลือกข้อความ" ผลคือทั้งพื้นที่
 *   กระดาษทดถูกไฮไลต์ฟ้าพร้อมจุดจับ และเด้งเมนู คัดลอก · แปลภาษา · ค้นหาในเว็บ
 *   ขึ้นมาทับขณะเด็กกำลังเขียนอยู่
 *
 *   ตอนนี้ `preventDefault()` **ทั้งสองทาง** — ทั้งตอนรับเส้นและตอนปฏิเสธนิ้ว
 *   บวก `contextmenu` ที่ถูกกินด้วย ดู 10-INFRA §12.14
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { translate as t } from '@/i18n'

import {
  MAX_BYTES,
  PEN_VARS,
  type PenIndex,
  type Point,
  type Stroke,
  WIDTHS,
  byteSize,
  fitCanvas,
  hitTest,
  paintAll,
  roundPoint,
} from './strokes'

const CANVAS_HEIGHT = 310

/** ความลึกของประวัติ — พอสำหรับกู้ความผิดพลาด ไม่กินหน่วยความจำจนน่ากลัว */
const HISTORY_DEPTH = 30

interface Props {
  /** รอยเขียนที่บันทึกไว้ของข้อนี้ */
  value: Stroke[]
  /** เรียกทุกครั้งที่ปล่อยปากกา — ผู้เรียกเอาไปเขียนลง IndexedDB ทันที */
  onCommit: (strokes: Stroke[]) => void
  /** ให้พ่อแม่ของคอมโพเนนต์รู้ว่ากำลังเขียนอยู่ (ใช้หรี่/เร่งแสงโคมไฟ) */
  onLatency?: ((ms: number) => void) | undefined
  allowFinger: boolean
  onAllowFingerChange: (value: boolean) => void
  /**
   * ถ้ามีค่า = แถบเครื่องมือมีปุ่ม "ซ่อน" ที่ปลายขวา (24 ก.ย. 2569)
   * ตัวสถานะเปิด/ปิดอยู่ที่ QuizPage ไม่ใช่ที่นี่ เพราะคอมโพเนนต์นี้ถูก
   * `key={question_id}` ทุกข้อ ถ้าเก็บไว้ข้างในจะรีเซ็ตทุกครั้งที่เปลี่ยนข้อ
   */
  onCollapse?: (() => void) | undefined
}

export function Scratchpad({
  value,
  onCommit,
  onLatency,
  onCollapse,
  allowFinger,
  onAllowFingerChange,
}: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null)

  // ⚠️ เก็บสถานะการวาดใน ref ไม่ใช่ state — การวาดเกิดถี่ระดับ 240 Hz
  // ถ้าใช้ state จะ re-render ทุกจุดแล้วหน่วงทันที
  const strokesRef = useRef<Stroke[]>(value)
  const currentRef = useRef<Stroke | null>(null)
  /** สถานะก่อนหน้าเป็นชั้น ๆ — ทำให้ "ย้อน" ย้อนการล้างได้ด้วย */
  const pastRef = useRef<Stroke[][]>([])
  const penSeenRef = useRef(false)
  const downAtRef = useRef(0)
  const glowTimer = useRef<number | undefined>(undefined)

  const [pen, setPen] = useState<PenIndex>(0)
  const [width, setWidth] = useState<number>(WIDTHS[0])
  const [erasing, setErasing] = useState(false)
  const [count, setCount] = useState(value.length)
  // ★ แยกจาก `count` โดยตั้งใจ — หลังกดล้าง `count` เป็น 0 แต่ต้องยังกดย้อนได้
  //   ถ้าใช้ `count` ตัวเดียวคุมทั้งสองปุ่ม ปุ่มย้อนจะถูกปิดพอดีตอนที่ต้องใช้มันที่สุด
  const [canUndo, setCanUndo] = useState(false)
  const [tooBig, setTooBig] = useState(false)
  const [latency, setLatency] = useState<number | null>(null)
  const [penMode, setPenMode] = useState(false)

  const redraw = useCallback(() => {
    const canvas = canvasRef.current
    const ctx = ctxRef.current
    if (canvas && ctx) paintAll(canvas, ctx, strokesRef.current)
  }, [])

  const resize = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    ctxRef.current = fitCanvas(canvas, CANVAS_HEIGHT)
    redraw()
  }, [redraw])

  // โหลดรอยเขียนที่บันทึกไว้ — ครั้งเดียวตอน mount
  //
  // ⚠️ **ห้ามใส่ `value` ใน deps** — `value` เป็นอาร์เรย์ที่เปลี่ยน identity
  // ทุกครั้งที่ปล่อยปากกา ถ้าใส่จะได้ผลสองอย่างที่ไม่ตั้งใจ:
  //   1. `resize()` → `fitCanvas()` → **ล้างผืนผ้าใบแล้ววาดใหม่ทั้งหมด**
  //      ทุกเส้นที่เขียน ซึ่งเป็นสิ่งที่ข้อ 3 ของกฎกระดาษทดห้ามไว้ตรง ๆ
  //   2. ตัวเลข latency ถูกรีเซ็ตเป็น "—" ทันทีที่วัดได้ จนอ่านค่าไม่ได้เลย
  // (การวัด latency จริงเป็นตัวจับบั๊กนี้ — ถ้าประเมินด้วยสายตาจะไม่มีวันเห็น)
  //
  // คอมโพเนนต์นี้ถูก `key={question_id}` จาก QuizPage อยู่แล้ว
  // สลับข้อ = remount = โหลดใหม่เอง จึงไม่ต้องเฝ้า `value`
  const initial = useRef(value)
  useEffect(() => {
    strokesRef.current = initial.current
    setCount(initial.current.length)
    setTooBig(byteSize(initial.current) > MAX_BYTES)
    resize()
  }, [resize])

  useEffect(() => {
    window.addEventListener('resize', resize)
    // หมุน iPad — orientationchange มาก่อนที่ layout จะเสร็จ จึงต้องรอ frame ถัดไป
    const onRotate = (): void => {
      window.requestAnimationFrame(resize)
    }
    window.addEventListener('orientationchange', onRotate)
    return () => {
      window.removeEventListener('resize', resize)
      window.removeEventListener('orientationchange', onRotate)
    }
  }, [resize])

  const commit = useCallback(() => {
    const strokes = strokesRef.current
    setCount(strokes.length)
    setTooBig(byteSize(strokes) > MAX_BYTES)
    onCommit(strokes)
  }, [onCommit])

  const lamp = useCallback(() => {
    const wrap = wrapRef.current
    if (!wrap) return
    wrap.classList.add('writing')
    window.clearTimeout(glowTimer.current)
    glowTimer.current = window.setTimeout(() => wrap.classList.remove('writing'), 1400)
  }, [])

  /** ★ กันฝ่ามือ — เจอปากกาแล้วปฏิเสธนิ้วทันที เว้นแต่เปิดสวิตช์ไว้ */
  const accepts = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>): boolean => {
      if (event.pointerType === 'pen') {
        if (!penSeenRef.current) {
          penSeenRef.current = true
          setPenMode(true)
        }
        return true
      }
      if (event.pointerType === 'touch') return allowFinger || !penSeenRef.current
      return true
    },
    [allowFinger],
  )

  const toPoint = useCallback(
    (event: { clientX: number; clientY: number; pressure?: number }): Point => {
      const canvas = canvasRef.current
      if (!canvas) return roundPoint(0, 0, 0.5)
      const rect = canvas.getBoundingClientRect()
      // แรงกด: อุปกรณ์ที่ไม่รองรับคืน 0 — ใช้ค่าคงที่ 0.5 แทน (D7)
      const pressure = event.pressure && event.pressure > 0 ? event.pressure : 0.5
      return roundPoint(
        (event.clientX - rect.left) / rect.width,
        (event.clientY - rect.top) / rect.height,
        pressure,
      )
    },
    [],
  )

  /**
   * ★ "ย้อน" ต้องย้อน **การล้าง** ได้ด้วย ไม่ใช่แค่ถอนเส้นล่าสุด
   *
   * เดิมปุ่มล้างตั้ง `strokes = []` แล้วปุ่มย้อนทำ `.slice(0, -1)` บนอาเรย์ว่าง
   * ซึ่ง return ทันที = เด็กเขียนมาทั้งข้อแล้วนิ้วไปโดนปุ่มล้าง งานหายถาวร
   * ตอนนี้เก็บสถานะก่อนหน้าไว้เป็นชั้น ๆ ทุกการเปลี่ยนแปลงจึงย้อนได้เหมือนกันหมด
   */
  const pushHistory = useCallback(() => {
    // จำกัดความลึกไว้ ไม่งั้นกระดาษที่เขียนยาว ๆ กินหน่วยความจำไปเรื่อย
    pastRef.current = [...pastRef.current.slice(-(HISTORY_DEPTH - 1)), strokesRef.current]
    setCanUndo(true)
  }, [])

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      // ★ กินเหตุการณ์ **ก่อน** ตัดสินใจว่าจะวาดหรือไม่
      //   ถ้ากินเฉพาะตอนวาด นิ้วที่ถูกปฏิเสธจะกลายเป็นการกดค้างของ Safari
      event.preventDefault()
      if (!accepts(event)) return
      canvasRef.current?.setPointerCapture(event.pointerId)
      lamp()
      downAtRef.current = performance.now()

      const point = toPoint(event)
      if (erasing) {
        const index = hitTest(strokesRef.current, point[0], point[1])
        if (index >= 0) {
          pushHistory()
          strokesRef.current = strokesRef.current.filter((_, i) => i !== index)
          redraw()
          commit()
        }
        return
      }
      pushHistory()
      const stroke: Stroke = { c: pen, w: width, p: [point] }
      strokesRef.current = [...strokesRef.current, stroke]
      currentRef.current = stroke
    },
    [accepts, commit, erasing, lamp, pen, pushHistory, redraw, toPoint, width],
  )

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      // ลากนิ้วบนกระดาษทดต้องไม่กลายเป็นการลากเลือกข้อความ แม้ไม่ได้กำลังวาด
      event.preventDefault()
      const stroke = currentRef.current
      const canvas = canvasRef.current
      const ctx = ctxRef.current
      if (!stroke || !canvas || !ctx) return

      // ★ 2. ดึงจุดที่ถูกยุบรวม — นี่คือสาเหตุที่เส้นคม
      const native = event.nativeEvent
      const events: Array<{ clientX: number; clientY: number; pressure?: number }> =
        typeof native.getCoalescedEvents === 'function'
          ? native.getCoalescedEvents()
          : [native]

      const w = canvas.clientWidth
      const h = canvas.clientHeight
      ctx.strokeStyle =
        getComputedStyle(document.documentElement).getPropertyValue(PEN_VARS[stroke.c]).trim() ||
        '#000'
      ctx.lineWidth = stroke.w

      // ★ 3. วาดเฉพาะช่วงใหม่ ไม่ redraw ทั้งหมด
      const last = stroke.p[stroke.p.length - 1]
      if (!last) return
      ctx.beginPath()
      ctx.moveTo(last[0] * w, last[1] * h)
      for (const raw of events) {
        const point = toPoint(raw)
        stroke.p.push(point)
        ctx.lineTo(point[0] * w, point[1] * h)
      }
      ctx.stroke()

      if (downAtRef.current) {
        const ms = Math.round(performance.now() - downAtRef.current)
        downAtRef.current = 0
        setLatency(ms)
        onLatency?.(ms)
      }
    },
    [onLatency, toPoint],
  )

  const onPointerUp = useCallback(() => {
    if (!currentRef.current) return
    currentRef.current = null
    commit()
  }, [commit])

  const undo = useCallback(() => {
    const previous = pastRef.current.at(-1)
    if (previous === undefined) return
    pastRef.current = pastRef.current.slice(0, -1)
    setCanUndo(pastRef.current.length > 0)
    strokesRef.current = previous
    redraw()
    commit()
  }, [commit, redraw])

  const clearAll = useCallback(() => {
    if (strokesRef.current.length === 0) return
    // ล้างทั้งหน้าต้องยืนยันเสมอ (D7) — แต่ตอนนี้กดย้อนเอาคืนได้
    if (!window.confirm(t('pad.confirmClear'))) return
    pushHistory()
    strokesRef.current = []
    redraw()
    commit()
  }, [commit, pushHistory, redraw])

  return (
    // ★ กิน contextmenu ทั้งบล็อก — บน iOS การกดค้างมาเป็น contextmenu
    //   ถ้าไม่กิน จะได้เมนู คัดลอก/แปลภาษา/ค้นหาในเว็บ ทับกระดาษทด
    <div
      className="padwrap"
      id="padregion"
      ref={wrapRef}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="padbar">
        <span className="lbl">{t('pad.title')}</span>
        {PEN_VARS.map((cssVar, index) => (
          <button
            key={cssVar}
            type="button"
            className={`tool${pen === index && !erasing ? ' is-on' : ''}`}
            title={t(`pad.pen${index}`)}
            aria-label={t(`pad.pen${index}`)}
            aria-pressed={pen === index && !erasing}
            onClick={() => {
              setPen(index as PenIndex)
              setErasing(false)
            }}
          >
            <span className="pen" style={{ background: `var(${cssVar})` }} />
          </button>
        ))}
        <span className="sep" />
        {WIDTHS.map((value_, index) => (
          <button
            key={value_}
            type="button"
            className={`tool${width === value_ ? ' is-on' : ''}`}
            aria-pressed={width === value_}
            onClick={() => setWidth(value_)}
          >
            {t(index === 0 ? 'pad.thin' : 'pad.thick')}
          </button>
        ))}
        <span className="sep" />
        <button
          type="button"
          className={`tool${erasing ? ' is-on' : ''}`}
          aria-pressed={erasing}
          onClick={() => setErasing((v) => !v)}
        >
          {t('pad.erase')}
        </button>
        <button type="button" className="tool" onClick={undo} disabled={!canUndo}>
          {t('pad.undo')}
        </button>
        <button type="button" className="tool" onClick={clearAll} disabled={count === 0}>
          {t('pad.clear')}
        </button>
        {onCollapse ? (
          <>
            <span className="sep" />
            <button
              type="button"
              className="tool"
              data-testid="pad-hide"
              aria-expanded={true}
              aria-controls="padregion"
              onClick={onCollapse}
            >
              {t('pad.hide')} ▲
            </button>
          </>
        ) : null}
      </div>

      <div className="canvas-box">
        <div className="grid" />
        <div className="marginline" />
        <canvas
          className="sketch"
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onPointerLeave={onPointerUp}
        />
      </div>

      <div className="padfoot">
        <span className="dot" />
        <span>{penMode && !allowFinger ? t('pad.modePen') : t('pad.modeFinger')}</span>
        <span className="spacer" />
        <button
          type="button"
          className="link"
          onClick={() => onAllowFingerChange(!allowFinger)}
          style={{ padding: '0 8px', minHeight: 0 }}
        >
          {allowFinger ? t('pad.fingerOn') : t('pad.fingerOff')}
        </button>
        <span className="lat" data-testid="pad-latency">
          {latency === null ? '—' : t('pad.latency', 'th', { ms: latency })}
        </span>
      </div>

      {tooBig ? <div className="note">{t('pad.tooBig')}</div> : null}
    </div>
  )
}
